import { useEffect, useState } from 'react'
import { useChain } from '@cosmos-kit/react'
import { defaultChainName } from '../config'
import { Box, Center, Flex, Grid, Text, useToast } from '@chakra-ui/react'
import Section from './section'
import CoinBadge from './coinBadge'
import ButtonBadge from './buttonBadge'
import Spinner from './loading'
import Countdown from './countdown'
import PercentageBadge from './percentageBadge'
import { RewardsResponsePayload } from '../app/(juno)/api/rewards/[address]/route'

const tokenName = 'USDC'

export default function RewardsDetail() {
  const { address, isWalletConnected, getCosmWasmClient, getSigningCosmWasmClient } =
    useChain(defaultChainName)
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false)
  const [userStagesData, setUserStagesData] = useState<RewardsResponsePayload | null>(null)
  const [lastStage, setLastStage] = useState<number>(0)
  const [lastStageClaimed, setLastStageClaimed] = useState<number>(0)
  const [previousStagesClaimed, setPreviousStagesClaimed] = useState<{
    [key: number]: number
  }>({})
  const [totalClaimed, setTotalClaimed] = useState<number>(0)
  const [userCurrentAlreadyClaimed, setUserCurrentAlreadyClaimed] = useState<boolean>(false)
  const [userPreviousClaimed, setUserPreviousClaimed] = useState({})
  const [userTotalClaimed, setUserTotalClaimed] = useState<number>(0)
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [claimableNow, setClaimableNow] = useState<number>(0)
  const [isClaiming, setIsClaiming] = useState<boolean>(false)
  const [totalStaked, setTotalStaked] = useState<number>(0)
  const [userStaked, setUserStaked] = useState<number>(0)
  const [currentStageTotalDistribution, setCurrentStageTotalDistribution] = useState<number>(0)
  const toast = useToast()

  const rewardsContractAddress = process.env.NEXT_PUBLIC_REWARDS_USDC_CONTRACT_ADDRESS!
  const stakingContractAddress = process.env.NEXT_PUBLIC_STAKING_CONTRACT_ADDRESS!
  const decimals = Number(process.env.NEXT_PUBLIC_REWARDS_USDC_DECIMALS!)

  useEffect(() => {
    if (!isWalletConnected || !address) return

    const fetchUserStagesData = async () => {
      try {
        const response = await fetch(`/api/rewards/${address}`, {
          method: 'GET',
        })
        return (await response.json()) as RewardsResponsePayload
      } catch (e) {
        console.error(e)
      }
    }

    const fetchLatestStage = async (data: RewardsResponsePayload) => {
      if (!data) return
      try {
        const client = await getCosmWasmClient()
        const response = await client.queryContractSmart(rewardsContractAddress, {
          latest_stage: {},
        })
        const stage = Number(response.latest_stage)
        return {
          claimableNow: data[stage] ? data[stage]!.amount : 0,
          latestStage: stage,
        }
      } catch (e) {
        console.error(e)
        return {
          claimableNow: 0,
          latestStage: 0,
        }
      }
    }

    const fetchStageTotalClaimed = async (stageNumber: number) => {
      try {
        const client = await getCosmWasmClient()
        const response = await client.queryContractSmart(rewardsContractAddress, {
          total_claimed: { stage: stageNumber },
        })
        return Number(response.total_claimed)
      } catch (e) {
        console.error(e)
        return 0
      }

      // setLastStageClaimed(balance)
    }

    const fetchPreviousStagesClaimed = async (stageNumber: number) => {
      try {
        const client = await getCosmWasmClient()
        const acc: {
          [key: number]: number
        } = {}
        for (let i = 1; i <= stageNumber; i++) {
          const response = await client.queryContractSmart(rewardsContractAddress, {
            total_claimed: { stage: i },
          })
          acc[i] = Number(response.total_claimed)
        }

        const historyTotal = Object.values(acc).reduce((a, b) => a + b, 0)
        //setTotalClaimed(historyTotal)
        return {
          previousStagesClaimed: acc,
          historyTotal: historyTotal,
        }
      } catch (e) {
        console.error(e)
      }
    }

    const fetchUserAlreadyClaimed = async (stageNumber: number, userData: any | undefined) => {
      try {
        const client = await getCosmWasmClient()
        const acc: {
          [key: number]: boolean
        } = {}
        for (let i = 1; i <= stageNumber; i++) {
          const response = await client.queryContractSmart(rewardsContractAddress, {
            is_claimed: { stage: i, address },
          })
          acc[i] = response.is_claimed
        }

        const previous = Object.values(acc).slice(0, -1)

        let total = 0
        Object.keys(userData!).forEach(idx => {
          const index = Number(idx)
          const stageData = userData[index]
          const temp = stageData!.amount
          if (temp && acc[index]) {
            total += Number(temp)
          }
        })

        return {
          userCurrentAlreadyClaimed: acc[stageNumber],
          previous: previous,
          userTotalClaimed: total,
        }
      } catch (e) {
        console.error(e)
      }
    }

    const fetchStageInfo = async (stageNumber: number) => {
      try {
        const client = await getCosmWasmClient()
        const response = await client.queryContractSmart(rewardsContractAddress, {
          merkle_root: { stage: stageNumber },
        })
        const expiration = Number(response.expiration.at_time)
        const start = Number(response.start.at_time)

        const expirationDate = new Date(expiration / 1000000)
        // setExpirationDate(expirationDate)
        const startDate = new Date(start / 1000000)
        // setStartDate(startDate)
        return {
          expirationData: expirationDate,
          startDate: startDate,
        }
      } catch (e) {
        console.error(e)
        return {
          expirationData: new Date(),
          startDate: new Date(),
        }
      }
    }

    const fetchTotalStaked = async () => {
      const client = await getCosmWasmClient()
      try {
        const response = await client.queryContractSmart(stakingContractAddress, {
          total_staked_at_height: {},
        })
        const totalStaked = Number(response.total)
        return totalStaked ?? 0
      } catch (e) {
        console.error(e)
        return 0
      }
    }

    const fetchUserStaked = async () => {
      const client = await getCosmWasmClient()
      try {
        const response = await client.queryContractSmart(stakingContractAddress, {
          staked_balance_at_height: { address: address },
        })
        const userStaked = Number(response.balance)
        return userStaked ?? 0
      } catch (e) {
        console.error(e)
        return 0
      }
    }

    const fetchCurrentStageTotalDistribution = async (stage: number) => {
      try {
        const response = await fetch(`/api/rewards/totalAmount/${stage}`, {
          method: 'GET',
        })
        return ((await response.json()).amount as number) ?? 0
      } catch (e) {
        console.error(e)
      }
      return 0
    }

    const fetchData = async () => {
      setIsLoadingData(true)
      const userStagesData = await fetchUserStagesData()
      const latestStageData = await fetchLatestStage(userStagesData!)
      const lastStageTotalClaimed = await fetchStageTotalClaimed(latestStageData!.latestStage)
      const previousStagesClaimed = await fetchPreviousStagesClaimed(latestStageData!.latestStage)
      const userAlreadyClaimed = await fetchUserAlreadyClaimed(
        latestStageData!.latestStage,
        userStagesData,
      )
      const stageInfo = await fetchStageInfo(latestStageData!.latestStage)
      const totalStaked = await fetchTotalStaked()
      const userStaked = await fetchUserStaked()
      const currentStageTotalDistribution = await fetchCurrentStageTotalDistribution(
        latestStageData!.latestStage,
      )

      setUserStagesData(userStagesData!)
      setLastStage(latestStageData!.latestStage)
      setClaimableNow(latestStageData!.claimableNow!)
      setLastStageClaimed(lastStageTotalClaimed!)
      setPreviousStagesClaimed(previousStagesClaimed!.previousStagesClaimed)
      setTotalClaimed(previousStagesClaimed!.historyTotal)
      setUserCurrentAlreadyClaimed(userAlreadyClaimed!.userCurrentAlreadyClaimed!)
      if (userAlreadyClaimed!.userCurrentAlreadyClaimed) {
        setClaimableNow(0)
      }
      setUserPreviousClaimed(userAlreadyClaimed!.previous)
      setUserTotalClaimed(userAlreadyClaimed!.userTotalClaimed)
      setTotalStaked(totalStaked)
      setUserStaked(userStaked)
      setCurrentStageTotalDistribution(currentStageTotalDistribution)
      setExpirationDate(stageInfo!.expirationData)
      setStartDate(stageInfo!.startDate)
      setIsLoadingData(false)
    }

    fetchData().catch(console.error)
  }, [isWalletConnected, address, getCosmWasmClient, rewardsContractAddress])

  const claimRewards = async () => {
    if (!isWalletConnected || !address) return

    setIsClaiming(true)
    const userData = userStagesData![lastStage]
    const msg = {
      claim: {
        stage: lastStage,
        amount: userData?.amount.toString(),
        proof: userData?.proofs,
      },
    }

    try {
      const signingClient = await getSigningCosmWasmClient()
      const response = await signingClient.execute(address, rewardsContractAddress, msg, {
        gas: '350000',
        amount: [{ denom: 'ujuno', amount: '350000' }],
      })

      if (response.logs.length > 0) {
        toast({
          title: 'Rewards claimed',
          description: `Successfully claimed ${userData?.amount! / 1000000} USDC`,
          status: 'success',
          duration: 9000,
          isClosable: true,
        })
      }
    } catch (error: any) {
      console.error(error)
      let errorMessage = ''
      if (error.message.includes('Begins at scheduled time')) {
        errorMessage = 'Claiming window has not yet started'
      } else if (error.message.includes('expired')) {
        errorMessage = 'Rewards stage expired '
      } else if (error.message.includes('Already claimed')) {
        errorMessage = 'Already claimed'
      } else {
        errorMessage = error.message
      }
      toast({
        title: 'Claiming failed',
        description: errorMessage,
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    } finally {
      setIsClaiming(false)
    }
  }

  return (
    <Box maxW={{ xl: 'container.xl', base: '$100vh' }} mx="auto" p={{ xl: 20, base: 4 }}>
      {isLoadingData ? (
        <Flex position={'fixed'} top={0} left={0} right={0} bottom={0} zIndex={1000}>
          <Spinner />
        </Flex>
      ) : (
        <Box flexDir={{ lg: 'row', base: 'column' }}>
          <Box>
            <Center>
              <Box pb={2}>
                <Text
                  as={'span'}
                  fontSize={{ sm: 'xl', lg: '6xl' }}
                  fontWeight={700}
                  p={4}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Revenue Share
                </Text>
              </Box>
            </Center>
            <Box mt={8}>
              <Grid templateColumns={{ base: '1fr', lg: 'repeat(1, 2fr)' }} gap={12}>
                <Section>
                  <PercentageBadge
                    label="your %age of revenue share (staked amount in DAO DAO)"
                    amount={(userStaked / totalStaked) * 100}
                    enabled={true}
                  />
                  <CoinBadge
                    label="total revenue share this month"
                    amount={currentStageTotalDistribution}
                    denom="$USDC"
                    minDenom="$uUSDC"
                    decimals={decimals}
                    enabled={true}
                  />
                </Section>
                <Section>
                  <CoinBadge
                    label="your unclaimed revenue share this month"
                    amount={claimableNow}
                    denom="$USDC"
                    minDenom="$uUSDC"
                    decimals={decimals}
                    enabled={!userCurrentAlreadyClaimed}
                  />
                  <CoinBadge
                    label="claimed by you so far (in all stages)"
                    amount={userTotalClaimed}
                    denom="$USDC"
                    minDenom="$uUSDC"
                    decimals={decimals}
                    enabled={true}
                  />
                </Section>
                {claimableNow !== 0 && startDate && expirationDate && (
                  <Center>
                    <Box pt={4} borderColor={'tertiary.base'} borderWidth={3} width={'fit-content'}>
                      <Section>
                        <Box alignItems={'center'}>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width={300}
                          >
                            <Countdown targetDate={expirationDate} />
                          </Box>
                        </Box>
                        <Box alignItems={'center'}>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width={300}
                          >
                            <Text as={'span'} padding={2}>
                              $USDC rewards can be claimed starting on February 5, 2024. The current
                              stage starts on{' '}
                              {startDate.toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}{' '}
                              and expires on{' '}
                              {expirationDate.toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </Text>
                          </Box>
                        </Box>
                      </Section>
                      <Center>
                        <ButtonBadge
                          onClickHandle={claimRewards}
                          text="Claim rewards"
                          isLoading={isClaiming}
                        />
                      </Center>
                    </Box>
                  </Center>
                )}
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
